/**
 * AccountBackOrdersPage - /account/back-orders (all stores).
 *
 * Read-only status grid of back orders across every store the user can access, mirroring
 * AccountOrdersPage (store selector + status tabs + pagination), for the account layout.
 */
import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import authService from "../../services/authService";
import { BACKORDER_STATUS_TABS, BackOrderRow } from "../../components/backorder/BackOrdersGrid";

const AccountBackOrdersPage = () => {
  const [activeTab, setActiveTab] = useState("open");
  const [selectedStoreId, setSelectedStoreId] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const stores = authService.getAvailableStores() || [];
  const clientIds = stores.map((s) => s.id).filter(Boolean); // store.id is the clientId

  const buildQuery = useCallback(
    (statusFilter) => {
      let filter = "";
      if (selectedStoreId === "all") {
        if (clientIds.length > 0) {
          filter = `ClientID in (${clientIds.join(",")})`;
        }
      } else {
        filter = `ClientID eq ${selectedStoreId}`;
      }

      if (statusFilter) {
        filter = filter ? `${filter} and ${statusFilter}` : statusFilter;
      }

      return {
        $filter: filter,
        $orderby: "DateCreated desc",
        $expand: "ProductSkuVariant($select=ID,Name,LegacySKU,Flavor),Client($select=ID,Name)",
        $top: pageSize,
        $skip: (page - 1) * pageSize,
        $count: true,
      };
    },
    [clientIds, selectedStoreId, page, pageSize],
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["accountBackOrders", clientIds.join(","), selectedStoreId, activeTab, page],
    queryFn: async () => {
      if (clientIds.length === 0) return { backOrders: [], totalCount: 0 };
      const statusConfig = BACKORDER_STATUS_TABS[activeTab];
      const response = await apiService.get("/storefront-odata/StorefrontBackOrders", buildQuery(statusConfig.filter));
      return {
        backOrders: response.value || [],
        totalCount: response["@odata.count"] || 0,
      };
    },
    enabled: clientIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  const backOrders = data?.backOrders || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  const showStore = selectedStoreId === "all" && stores.length > 1;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handleStoreChange = (storeId) => {
    setSelectedStoreId(storeId);
    setPage(1);
  };

  if (stores.length === 0) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="mb-4">
              <i className="bi bi-shop display-1 text-muted"></i>
            </div>
            <h2 className="mb-3">No Stores Available</h2>
            <p className="text-muted mb-4">You don&apos;t have access to any stores yet.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="mb-1">All Back Orders</h1>
              <p className="text-muted mb-0">View back orders across all your stores</p>
            </div>
          </div>
        </div>
      </div>

      {/* Store Filter */}
      {stores.length > 1 && (
        <div className="row mb-4">
          <div className="col-md-4">
            <label className="form-label fw-semibold">
              <i className="bi bi-shop me-2"></i>
              Filter by Store
            </label>
            <select
              className="form-select"
              value={selectedStoreId}
              onChange={(e) => handleStoreChange(e.target.value)}
            >
              <option value="all">All Stores ({stores.length})</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Status Tabs */}
      <div className="row mb-4">
        <div className="col">
          <ul className="nav nav-tabs">
            {Object.entries(BACKORDER_STATUS_TABS).map(([key, config]) => (
              <li className="nav-item" key={key}>
                <button
                  className={`nav-link ${activeTab === key ? "active" : ""}`}
                  onClick={() => handleTabChange(key)}
                >
                  {config.label}
                  {activeTab === key && totalCount > 0 && (
                    <span className="badge bg-primary ms-2">{totalCount}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="alert alert-danger mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Failed to load back orders. Please try again.
          <button className="btn btn-link p-0 ms-2" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading back orders...</p>
        </div>
      ) : backOrders.length === 0 ? (
        <div className="text-center py-5">
          <i className={`bi ${BACKORDER_STATUS_TABS[activeTab].emptyIcon} display-1 text-muted mb-3 d-block`}></i>
          <h4 className="text-muted mb-3">{BACKORDER_STATUS_TABS[activeTab].emptyMessage}</h4>
          <p className="text-muted mb-0">Back orders appear here as you request out-of-stock products.</p>
        </div>
      ) : (
        <>
          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Back Order #</th>
                    {showStore && <th>Store</th>}
                    <th>Date Requested</th>
                    <th>Product</th>
                    <th className="text-center">Qty</th>
                    <th className="text-end">Est. Total</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {backOrders.map((bo) => (
                    <BackOrderRow key={bo.id} backOrder={bo} showStore={showStore} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <p className="text-muted mb-0">
                Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} back orders
              </p>
              <nav aria-label="Back orders pagination">
                <ul className="pagination mb-0">
                  <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                  </li>
                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (page <= 3) {
                      pageNum = idx + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = page - 2 + idx;
                    }
                    return (
                      <li key={pageNum} className={`page-item ${page === pageNum ? "active" : ""}`}>
                        <button className="page-link" onClick={() => setPage(pageNum)}>
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AccountBackOrdersPage;
